/**
 * Shared geometry types for the WebGL2 metro map renderer.
 *
 * Mirrors the Android (`feature/metro-map/render/RenderFloor.kt`) and
 * iOS Metal (`MetroModules/Sources/MetroMap/Rendering/MetroMapRenderFloor.swift`)
 * counterparts. Vertex layout matches `feature/metro-map/render/Tessellator.kt`
 * (interleaved position + RGBA color, 6 floats per vertex) so that triangulation
 * output stays portable across platforms when the Phase 2 `PrecomputedProvider`
 * lands.
 */

export type Theme = 'light' | 'dark'

/**
 * A geometry set of the map bundle: the day map in a {@link Theme}, or the
 * late-night frame (`night`, TASK-1136 — its own MMTG files, dark palette).
 */
export type MapVariant = Theme | 'night'

/**
 * Render layer priorities — painter's algorithm. Lower value is drawn first
 * and ends up visually behind higher values.
 *
 * Numeric raw values MUST stay in lock-step with:
 *   - metro-android `feature/metro-map/.../render/RenderFloor.kt`
 *   - metro-ios `MetroModules/Sources/MetroMap/Rendering/MetroMapRenderFloor.swift`
 */
export const RenderFloor = {
  ADDITIONAL: 0,
  CONNECTION_UNDERGROUND: 100,
  CONNECTION_TRAM: 110,
  CONNECTION_CABLE_CAR: 120,
  CONNECTION_DEFAULT: 130,
  CONNECTION_ELIZABETH_LINE: 140,
  CONNECTION_DLR: 150,
  CONNECTION_OVERGROUND: 160,
  CONNECTION_NATIONAL_RAIL: 170,
  STATION: 300,
  /** TASK-236: drawn on top of stations — transfer tunnels between stations. */
  TRANSITION: 400,
  /**
   * Topmost map layer — station name labels (`SchemaText`). Drawn above
   * everything else so labels stay readable. iOS / Android phases of
   * TASK-237 must add the matching `text = 500` value to their
   * `MetroMapRenderFloor` / `RenderFloor` enums (load-bearing
   * cross-platform constant).
   */
  TEXT: 500,
} as const

export type RenderFloor = (typeof RenderFloor)[keyof typeof RenderFloor]

/**
 * Number of float32 values per interleaved vertex: position (x, y) + RGBA.
 * Matches Android `Tessellator` and iOS `MetroMapPolygonTriangulation` output.
 */
export const FLOATS_PER_VERTEX = 6

/**
 * Byte stride per interleaved vertex (`FLOATS_PER_VERTEX * 4`). Used as the
 * `stride` argument for `gl.vertexAttribPointer` when binding the position and
 * color attributes from a single VBO.
 */
export const BYTES_PER_VERTEX = FLOATS_PER_VERTEX * 4

/**
 * GPU-ready vertex data for a single map element (connection, station, or
 * additional). Modeled after Android `ConnectionParseResult`.
 *
 * Both buffers use the interleaved `[x, y, r, g, b, a, ...]` layout described
 * by `FLOATS_PER_VERTEX`. Either buffer may be empty (length 0) when the
 * element has no fill or no stroke.
 */
export interface ElementGeometry {
  readonly id: string
  readonly fillVertices: Float32Array
  readonly strokeVertices: Float32Array
  readonly renderFloor: RenderFloor
}

/**
 * A complete pre-tessellated map for a given theme. Returned from
 * `GeometryProvider.load`. The renderer consumes one `GeometrySet` per active
 * theme; theme switching swaps the active set without re-triangulation.
 */
export interface GeometrySet {
  readonly version: string
  readonly theme: Theme
  readonly elements: readonly ElementGeometry[]
  /**
   * Transitions flagged with `skipInitialDraw: true` — tessellated and
   * ready for instant retrieval (e.g. route overlay showing a specific
   * transfer) but NOT included in `elements` so the renderer doesn't
   * draw them on initial load. Keyed by transition id.
   *
   * Cross-platform parity: Android stores skip entries in the `.mmtg`
   * cache but filters them from the render list in
   * `materialiseTransitions`; iOS processes them in the pipeline but
   * excludes them from the MTLBuffer tagged array.
   */
  readonly hiddenTransitions?: ReadonlyMap<string, readonly ElementGeometry[]>
  /**
   * Texts flagged with `skipInitialDraw: true` — symmetric with
   * `hiddenTransitions`. Tessellated at load and held in memory so the
   * route highlight overlay (or any future on-demand text consumer)
   * can lift them without a second tessellation pass. NOT included in
   * `elements` — the main draw pass leaves them out so the base map
   * stays uncluttered.
   *
   * Keyed by text id (e.g. `text-vic-OXC`). Today the
   * `RouteHighlightOverlay` reads schema text `svgPath` strings
   * directly so this map is defensive parity infrastructure: it mirrors
   * iOS's pipeline shape (commit `e745d327` — keep all entries, gate
   * at draw time) and prevents a regression when a future refactor
   * moves text highlight rendering from the SVG overlay path into the
   * WebGL2 buffer pipeline. See QA-routing-v2 audit W2-2.
   */
  readonly hiddenTexts?: ReadonlyMap<string, readonly ElementGeometry[]>
}
